import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import MetaAgentAPI from 'dashboard/api/metaAgent/agent';

/**
 * Thread control for conversations attended by Meta Business Agent.
 *
 * Meta has no "take control" endpoint — replying is what takes the thread — so
 * the only explicit action is handing it back. Ownership is tracked server-side
 * from webhook route evidence and surfaced on the conversation.
 */
export function useMetaAgentThread() {
  const { t } = useI18n();
  const currentChat = useMapGetter('getSelectedChat');

  const isReturning = ref(false);

  const owner = computed(
    () => currentChat.value?.additional_attributes?.meta_agent_owner
  );

  const isMetaAgentAttending = computed(() => owner.value === 'meta');
  const canReturnToMetaAgent = computed(() => owner.value === 'app');

  const returnToMetaAgent = async () => {
    if (isReturning.value) return;
    isReturning.value = true;
    try {
      await MetaAgentAPI.releaseThread(currentChat.value.id);
      useAlert(t('CONVERSATION.META_AGENT_THREAD.RETURN_SUCCESS'));
    } catch (error) {
      const data = error?.response?.data || {};
      // A timeout or 5xx leaves the release genuinely undecided: it may have
      // landed at Meta. Saying "could not hand back" would invite a retry that
      // then fails as already-released, so the ambiguity is stated instead.
      useAlert(
        data.ambiguous
          ? t('CONVERSATION.META_AGENT_THREAD.RETURN_AMBIGUOUS')
          : data.error || t('CONVERSATION.META_AGENT_THREAD.RETURN_ERROR')
      );
    } finally {
      isReturning.value = false;
    }
  };

  return {
    isMetaAgentAttending,
    canReturnToMetaAgent,
    isReturning,
    returnToMetaAgent,
  };
}
